import { Link } from "react-router-dom";
import {
  ShieldCheck, CheckCircle2, FileText, Gauge, Wrench, Sparkles,
  Bell, ScrollText, ClipboardList, Clock, Building2, Star, ArrowRight, Lock,
} from "lucide-react";
import SEO from "@/components/SEO";

const PASS_UNLOCKS = [
  { icon: Gauge,        title: "Vehicle current state",
    body: "Public-safe operational status (available, booked, in use, maintenance, or needs recheck), shown separately from the booking's Release Readiness. An operational status alone never means the rental is ready for pickup." },
  { icon: Wrench,       title: "Known damages / digital damage sheet",
    body: "Read-only diagram with damages already recorded by the business: small scratches, dents, paint chips, cracked lights, tire/wheel issues, interior stains, and more." },
  { icon: ShieldCheck,  title: "Vehicle incident and maintenance history",
    body: "Open incidents, recently resolved issues, recent maintenance, and booking disruptions in the last 90 days, as recorded facts rather than a rating." },
  { icon: Star,         title: "Booking Readiness Summary",
    body: "The specific readiness states for your booking and their reasons: agreement accepted, payment or security-deposit condition, business and vehicle legal readiness, safety hold, and driver eligibility. No single High / Medium / Low score." },
  { icon: Building2,    title: "Business reliability signals",
    body: "Average rating, booking-linked review count, response speed, completed bookings, and only those readiness signals ConfirmPH has actually reviewed. A business's paid plan or Sponsored placement is never shown as a trust signal." },
  { icon: Clock,        title: "Fuller customer-side timeline",
    body: "Created → agreement confirmed → release and handover → active → changes or incidents if any → returned → inspection → settlement → closed, with timestamps." },
  { icon: Bell,         title: "Priority email + in-app alerts",
    body: "Get faster nudges if the booking is changed, the vehicle is flagged, or an incident is opened nearby." },
  { icon: FileText,     title: "Booking Summary page / download",
    body: "A printable Booking Summary with your booking reference, business, vehicle, dates, status, and the damages recorded at the time you viewed it. It is a record, not a government certificate or a tax invoice." },
];

const RENTER_PLAN_BENEFITS = [
  "Unlimited Booking Confidence Reports",
  "Saved bookings",
  "Priority alerts",
  "Business comparison",
  "Booking-record filters",
  "Full booking timelines",
  "Reusable renter profile",
];

const PRIVATE_DATA_HIDDEN = [
  "Internal staff notes",
  "Finance / admin records",
  "Other customers' details",
  "Previous renter names",
  "Private incident notes",
  "Business internal costs",
  "Staff performance data",
  "Raw permits, registration or insurance documents",
  "Driver licence details",
];

export default function CustomerPricing() {
  return (
    <div className="bg-white" data-testid="customer-pricing-page">
      <SEO
        title="Renter Pricing & Booking Confidence: ConfirmPH"
        description="Booking Confidence Pass: coming soon for renters who want deeper recorded vehicle, readiness, damage, and booking context."
        canonical="/pricing"
      />
      {/* HERO */}
      <section className="px-4 sm:px-6 lg:px-8 pt-14 pb-10 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
          <ShieldCheck className="w-3.5 h-3.5" /> For renters & customers
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold leading-tight">
          Extra booking confidence, when you need it.
        </h1>
        <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-2xl">
          Free ConfirmPH features already cover the essentials: finding your booking, confirming it,
          getting status updates, and basic incident warnings. For renters who want a bit more
          transparency, we are preparing a one-time pass per booking.
        </p>
      </section>

      {/* FREE BAR */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-testid="cp-free-tier">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h2 className="font-display text-lg sm:text-xl font-semibold">Always free for customers</h2>
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Finding your booking, confirming details, leaving booking-linked reviews, and getting
            basic incident warnings will always be free. You only pay if you want the extended
            Booking Confidence Report on a specific rental.
          </p>
          <p className="text-sm text-slate-900 mt-2 leading-relaxed font-medium" data-testid="cp-no-paywall">
            Required legal, safety, driver and release-readiness checks are never paywalled. They
            apply to every booking whether or not anyone pays; a paid pass only adds presentation,
            context and alerts.
          </p>
        </div>
      </section>

      {/* MAIN: TWO TIERS */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12 max-w-5xl mx-auto grid lg:grid-cols-2 gap-6 items-start">
        {/* ONE-TIME PASS */}
        <article className="rounded-2xl border-2 border-[#1B4332]/20 bg-white p-6 shadow-sm relative"
                  data-testid="cp-pass-card">
          <div className="absolute -top-3 left-5">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-[#1B4332] text-white border border-[#1B4332]">
              Coming soon · Recommended starting point
            </span>
          </div>
          <h2 className="font-display text-2xl font-semibold">Booking Confidence Pass</h2>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold" data-testid="cp-pass-price">₱79</span>
            <span className="text-sm text-slate-500">per booking · one-time</span>
          </div>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed italic">
            “See more recorded rental context, including available vehicle history,
            public-safe verification and readiness information, damage records, and other renter
            due-diligence information. Paying never gives access to raw permits, licences or
            insurance documents.”
          </p>

          <div className="mt-5 space-y-3">
            {PASS_UNLOCKS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-2.5">
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#1B4332]/10 text-[#1B4332] flex items-center justify-center mt-0.5">
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <button type="button" disabled data-testid="cp-pass-cta"
                    className="w-full px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm inline-flex items-center justify-center gap-1.5 opacity-70 cursor-not-allowed">
              <Lock className="w-3.5 h-3.5" /> Coming soon: purchase from a booking
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              We'll show a “Get Booking Confidence Pass” button on each booking page when it goes live.
            </p>
          </div>

          <p className="text-xs text-slate-600 mt-4 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="cp-pass-disclaimer">
            <ScrollText className="w-3 h-3 inline -mt-0.5 mr-1" />
            This report is based on information recorded by the rental business in ConfirmPH.
            It helps improve transparency, but it is not a guarantee of vehicle condition or availability.
          </p>
        </article>

        {/* RENTER PLAN: COMING LATER */}
        <article className="rounded-2xl border border-slate-200 bg-white p-6 relative" data-testid="cp-plan-card">
          <div className="absolute -top-3 left-5">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              Future option · coming later
            </span>
          </div>
          <h2 className="font-display text-2xl font-semibold">Renter Plan</h2>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold text-slate-400" data-testid="cp-plan-price">₱149</span>
            <span className="text-sm text-slate-500">per month · indicative</span>
          </div>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed italic">
            “For renters, tourists, and expats who want extra confidence across multiple bookings.”
          </p>

          <ul className="mt-5 space-y-2 text-sm text-slate-700">
            {RENTER_PLAN_BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6">
            <button type="button" disabled data-testid="cp-plan-cta"
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-500 font-medium text-sm inline-flex items-center justify-center gap-1.5 cursor-not-allowed bg-slate-50">
              <Lock className="w-3.5 h-3.5" /> Coming later: notify me when ready
            </button>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              Launches after enough rental businesses join ConfirmPH so the plan is useful.
            </p>
          </div>
        </article>
      </section>

      {/* PRIVACY GUARDRAILS */}
      <section className="px-4 sm:px-6 lg:px-8 mt-14 max-w-5xl mx-auto" data-testid="cp-privacy-guardrails">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#1B4332]" />
            <h2 className="font-display text-lg sm:text-xl font-semibold">What you will <em>not</em> see, even with a paid pass</h2>
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            We deliberately hide anything that would compromise other customers or the rental
            business. Paid means transparency, not surveillance.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {PRIVATE_DATA_HIDDEN.map((d) => (
              <div key={d} className="flex items-start gap-2 text-sm text-slate-700">
                <Lock className="w-3.5 h-3.5 text-rose-500 mt-0.5 flex-shrink-0" />
                <span>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-8 mt-14 mb-20 max-w-5xl mx-auto" data-testid="cp-final-cta">
        <div className="rounded-2xl bg-[#1B4332] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Have a booking already?</h2>
            <p className="text-sm text-white/85 mt-1">
              Find it, confirm it, or request a written confirmation: all for free.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/find-booking" data-testid="cp-cta-find"
                   className="inline-flex items-center px-5 py-2.5 rounded-lg bg-white text-[#1B4332] font-medium text-sm hover:bg-slate-100">
              Find my booking <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
            <Link to="/request-confirmation" data-testid="cp-cta-request"
                   className="inline-flex items-center px-5 py-2.5 rounded-lg border border-white/40 text-white text-sm font-medium hover:bg-white/10">
              Request confirmation
            </Link>
            <Link to="/rentals" data-testid="cp-cta-rentals"
                   className="inline-flex items-center px-5 py-2.5 rounded-lg border border-white/40 text-white text-sm font-medium hover:bg-white/10">
              Discover rentals
            </Link>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1">
          <ClipboardList className="w-3 h-3" />
          Run a rental business? <Link to="/businesses/pricing" className="underline ml-1 hover:text-slate-800">See business plans</Link>.
        </p>
      </section>
    </div>
  );
}
